import * as React from 'react';
import { createContext, useEffect, useState } from 'react';

import API from 'js/lib/api';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import { get as getUser } from 'js/lib/user';

export type WeekStatus = {
  weekNumber: number;
  status: 'ONTRACK' | 'COMPLETED';
};

type DeadlineStatusesApiResponse = {
  weekStatuses: WeekStatus[];
};

export type DeadlineStatusesProviderContext = {
  deadlineStatuses?: WeekStatus[];
  loading: boolean;
  refreshData: () => void;
};

type PropsFromCaller = {
  children: React.ReactNode;
  courseId?: string;
  skip?: boolean;
};

const DeadlineStatusesContext = createContext<DeadlineStatusesProviderContext>({
  loading: false,
  refreshData: () => undefined,
});

const deadlineStatusesApi = API('/api/deadlineStatuses.v1', { type: 'rest' });

/*
 * We're using a provider instead of Naptime because the endpoint (action=getDeadlineStatusesForAllWeeks)
 * is a mutation that triggers a POST and is thus not callable by the Naptime HOC/hook, even though
 * the endpoint only returns data in a GET manner.
 *
 * The provider also allows caching the API request instead of calling it multiple times if implemented as a hook
 */
export const DeadlineStatusesProvider = ({ courseId, children, skip }: PropsFromCaller) => {
  const userId = getUser()?.id;
  const [deadlineStatuses, setDeadlineStatuses] = useState<WeekStatus[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [apiRequest, setApiRequest] = useState<Promise<DeadlineStatusesApiResponse> | undefined>();

  const fetchData = async (_courseId: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('action', 'getDeadlineStatusesForAllWeeks');
      params.append('id', tupleToStringKey([userId, _courseId]));
      const _apiRequest = deadlineStatusesApi.post(`?${params.toString()}`);
      setApiRequest(_apiRequest);
      const data: DeadlineStatusesApiResponse = await _apiRequest;
      setDeadlineStatuses(data.weekStatuses);
    } catch {
      setDeadlineStatuses(undefined);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    setApiRequest(undefined);
  };

  useEffect(() => {
    if (!courseId || skip || apiRequest) {
      return;
    }

    fetchData(courseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, skip, apiRequest]);

  return (
    <DeadlineStatusesContext.Provider value={{ loading, deadlineStatuses, refreshData }}>
      {children}
    </DeadlineStatusesContext.Provider>
  );
};

export const useDeadlineStatusesProvider = () => {
  // We want this to hook to be truly optional, so we do not throw an error if this is used outside of a DeadlineStatusesProvider
  const context = React.useContext(DeadlineStatusesContext);
  return context;
};

export default DeadlineStatusesProvider;
